import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Briefcase, Plus, Clock, CheckCircle2, XCircle, FileText, Eye, Edit2, Send, X, Trash2, Printer, CalendarIcon, MessageCircle } from 'lucide-react';
import { formatDate, cn } from '@/lib/utils';
import { format, parse } from 'date-fns';
import SPOPrint from '@/components/SPOPrint';
import { formatCurrency, formatRate } from '@/utils/formatters';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-gray-100 text-gray-600 border-gray-200',
  pending_approval: 'bg-orange-100 text-orange-700 border-orange-200',
  clarification_requested: 'bg-purple-100 text-purple-700 border-purple-200',
  approved: 'bg-blue-100 text-blue-700 border-blue-200',
  rejected: 'bg-red-100 text-red-500 border-red-200',
  issued: 'bg-green-100 text-green-700 border-green-200',
  complete: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-100 text-red-500 border-red-200'
};

const statusLabels = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  clarification_requested: 'Clarification Requested',
  approved: 'Approved',
  rejected: 'Rejected',
  issued: 'Issued',
  complete: 'Complete',
  cancelled: 'Cancelled'
};

export default function ServicePurchaseOrdersPage() {
  const { user, getAuthHeader } = useAuth();
  const [spos, setSpos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [summary, setSummary] = useState({});

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingSpo, setEditingSpo] = useState(null);
  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    service_description: '',
    service_items: [{ description: '', quantity: '', unit: 'NOS', rate: 0, amount: 0 }],
    gst_rate: 18,
    other_charges: 0,
    notes: '',
    advance_requested: false,
    advance_amount: 0,
    advance_payment_due_date: null  // Use Date object
  });
  const [saving, setSaving] = useState(false);

  // View detail dialog
  const [viewingSpo, setViewingSpo] = useState(null);

  // Clarification response state
  const [clarificationResponse, setClarificationResponse] = useState('');
  const [respondingToClarification, setRespondingToClarification] = useState(false);

  const handleRespondToClarification = async () => {
    if (!clarificationResponse.trim()) {
      toast.error('Please enter your response');
      return;
    }
    
    setRespondingToClarification(true);
    try {
      await axios.post(`${API_URL}/service-purchase-orders/${viewingSpo.id}/respond-clarification`, {
        response: clarificationResponse.trim()
      }, { headers: getAuthHeader() });
      toast.success('Response submitted. SPO resubmitted for approval.');
      setClarificationResponse('');
      setViewingSpo(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setRespondingToClarification(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [sposRes, suppliersRes] = await Promise.all([
        axios.get(`${API_URL}/service-purchase-orders`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() })
      ]);
      setSpos(sposRes.data.spos || []);
      setSummary(sposRes.data.summary || {});
      setSuppliers(suppliersRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // Fetch full SPO details (includes supplier_details for print)
  const fetchFullSPO = async (spoId) => {
    try {
      const response = await axios.get(`${API_URL}/service-purchase-orders/${spoId}`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch SPO details:', error);
      return null;
    }
  };

  // Open view dialog with full SPO data
  const openViewDialog = async (spo) => {
    const fullSpo = await fetchFullSPO(spo.id);
    setViewingSpo(fullSpo || spo);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Date helpers
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    // If in YYYY-MM-DD format, convert to DD/MM/YYYY
    if (dateStr.includes('-')) {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    }
    return dateStr;
  };

  const parseDateStringToDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      // If in YYYY-MM-DD format
      if (dateStr.includes('-')) {
        return new Date(dateStr);
      }
      return null;
    } catch {
      return null;
    }
  };

  const formatDateForBackend = (date) => {
    if (!date) return null;
    // Format as DDMMYYYY for backend
    return format(date, 'ddMMyyyy');
  };

  // Calculate totals
  const calculateTotals = (items, gstRate, otherCharges) => {
    const subTotal = items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
    const gstAmount = (subTotal * gstRate / 100);
    const grandTotal = subTotal + gstAmount + (otherCharges || 0);
    return { subTotal, gstAmount, grandTotal };
  };

  // Filtered SPOs
  const filteredSpos = spos.filter(spo => {
    if (filterStatus === 'all') return true;
    return spo.status === filterStatus;
  });

  // Handle form input change
  const handleInputChange = (field, value) => {
    // Convert to uppercase for text fields
    if (typeof value === 'string' && !['supplier_id', 'advance_payment_due_date'].includes(field)) {
      value = value.toUpperCase();
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle service item change
  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.service_items];
    if (field === 'description') {
      value = value.toUpperCase();
    }
    if (field === 'unit') {
      value = value.toUpperCase();
    }
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-calculate amount
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }
    
    setFormData(prev => ({ ...prev, service_items: newItems }));
  };

  // Add service item
  const addServiceItem = () => {
    setFormData(prev => ({
      ...prev,
      service_items: [...prev.service_items, { description: '', quantity: '', unit: 'NOS', rate: 0, amount: 0 }]
    }));
  };

  // Remove service item
  const removeServiceItem = (index) => {
    if (formData.service_items.length === 1) {
      toast.error('At least one service item is required');
      return;
    }
    const newItems = formData.service_items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, service_items: newItems }));
  };

  // Handle supplier selection
  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setFormData(prev => ({
      ...prev,
      supplier_id: supplierId,
      supplier_name: supplier?.name || ''
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      supplier_id: '',
      supplier_name: '',
      service_description: '',
      service_items: [{ description: '', quantity: '', unit: 'NOS', rate: 0, amount: 0 }],
      gst_rate: 18,
      other_charges: 0,
      notes: '',
      advance_requested: false,
      advance_amount: 0,
      advance_payment_due_date: null
    });
    setEditingSpo(null);
  };

  // Open form for create
  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  // Open form for edit
  const openEditForm = (spo) => {
    setEditingSpo(spo);
    setFormData({
      supplier_id: spo.supplier_id,
      supplier_name: spo.supplier_name,
      service_description: spo.service_description,
      service_items: spo.service_items || [{ description: '', quantity: '', unit: 'NOS', rate: 0, amount: 0 }],
      gst_rate: spo.gst_rate || 18,
      other_charges: spo.other_charges || 0,
      notes: spo.notes || '',
      advance_requested: spo.advance_requested || false,
      advance_amount: spo.advance_amount || 0,
      advance_payment_due_date: parseDateStringToDate(spo.advance_payment_due_date)
    });
    setShowForm(true);
  };

  // Submit form
  const handleSubmit = async () => {
    // Validation
    if (!formData.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }
    if (!formData.service_description) {
      toast.error('Please enter service description');
      return;
    }
    // Validate quantities
    for (let i = 0; i < formData.service_items.length; i++) {
      const item = formData.service_items[i];
      const qty = parseFloat(item.quantity);
      if (!item.quantity || isNaN(qty) || qty <= 0) {
        toast.error(`Service item ${i + 1}: Quantity is required`);
        return;
      }
    }
    if (formData.service_items.some(item => !item.description || item.rate <= 0)) {
      toast.error('Please fill all service items with valid rates');
      return;
    }
    if (formData.advance_requested && (!formData.advance_amount || formData.advance_amount <= 0)) {
      toast.error('Please enter advance amount');
      return;
    }
    if (formData.advance_requested && !formData.advance_payment_due_date) {
      toast.error('Please select advance payment due date');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        advance_payment_due_date: formatDateForBackend(formData.advance_payment_due_date),
        service_items: formData.service_items.map(item => ({
          ...item,
          amount: item.quantity * item.rate
        }))
      };

      if (editingSpo) {
        await axios.patch(`${API_URL}/service-purchase-orders/${editingSpo.id}`, payload, { headers: getAuthHeader() });
        toast.success('Service PO updated successfully');
      } else {
        await axios.post(`${API_URL}/service-purchase-orders`, payload, { headers: getAuthHeader() });
        toast.success('Service PO created and submitted for approval');
      }

      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Service PO');
    } finally {
      setSaving(false);
    }
  };

  // Handle issue
  const handleIssue = async (spo) => {
    try {
      await axios.post(`${API_URL}/service-purchase-orders/${spo.id}/issue`, {}, { headers: getAuthHeader() });
      toast.success('Service PO issued successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue Service PO');
    }
  };

  // Totals for current form
  const { subTotal, gstAmount, grandTotal } = calculateTotals(
    formData.service_items,
    formData.gst_rate,
    formData.other_charges
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Briefcase className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Service Purchase Orders</h1>
              <p className="text-sm text-gray-500">Manage service-based purchase orders</p>
            </div>
          </div>
          <Button onClick={openCreateForm} className="gap-2">
            <Plus className="w-4 h-4" />
            Create Service PO
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('all')}>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-900">{summary.total || 0}</div>
              <div className="text-sm text-gray-500">Total</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('pending_approval')}>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">{summary.pending_approval || 0}</div>
              <div className="text-sm text-gray-500">Pending Approval</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('approved')}>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{summary.approved || 0}</div>
              <div className="text-sm text-gray-500">Approved</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('issued')}>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{summary.issued || 0}</div>
              <div className="text-sm text-gray-500">Issued</div>
            </CardContent>
          </Card>
        </div>

        {/* SPO Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg">Service PO Register</CardTitle>
              {filterStatus !== 'all' && (
                <Button variant="ghost" size="sm" onClick={() => setFilterStatus('all')}>
                  Clear Filter
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : filteredSpos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No service purchase orders found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SPO No.</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Advance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSpos.map((spo) => (
                      <TableRow key={spo.id}>
                        <TableCell className="font-medium">{spo.spo_number}</TableCell>
                        <TableCell>{formatDateForDisplay(spo.created_at?.split('T')[0])}</TableCell>
                        <TableCell>{spo.supplier_name}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{spo.service_description}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(spo.grand_total || 0)}</TableCell>
                        <TableCell className="text-right">
                          {spo.advance_requested ? (
                            <span className="text-orange-600">{formatCurrency(spo.advance_amount || 0)}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusColors[spo.status]} border`}>
                            {statusLabels[spo.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openViewDialog(spo)} title="View Details">
                              <Eye className="w-4 h-4" />
                            </Button>
                            {['approved', 'issued', 'complete'].includes(spo.status) && (
                              <SPOPrint spo={spo} />
                            )}
                            {['draft', 'rejected', 'pending_approval'].includes(spo.status) && (
                              <Button variant="ghost" size="sm" onClick={() => openEditForm(spo)} title="Edit">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            {spo.status === 'approved' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleIssue(spo)}
                                title="Issue to Supplier"
                                className="text-green-600"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Form Dialog */}
        <Dialog open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); resetForm(); } }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSpo ? 'Edit Service PO' : 'Create Service PO'}</DialogTitle>
              <DialogDescription>
                {editingSpo ? 'Update service purchase order details' : 'Create a new service purchase order for approval'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Supplier Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Supplier *</Label>
                  <SearchableSelect
                    options={suppliers.map(s => ({ value: s.id, label: s.name }))}
                    value={formData.supplier_id}
                    onValueChange={handleSupplierChange}
                    placeholder="Select supplier"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Service Description *</Label>
                  <Input
                    value={formData.service_description}
                    onChange={(e) => handleInputChange('service_description', e.target.value)}
                    placeholder="E.g., ANNUAL MAINTENANCE CONTRACT"
                    className="uppercase"
                  />
                </div>
              </div>

              {/* Service Items */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Service Items</Label>
                  <Button variant="outline" size="sm" onClick={addServiceItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40%]">Description</TableHead>
                        <TableHead className="w-[12%]">Qty</TableHead>
                        <TableHead className="w-[12%]">Unit</TableHead>
                        <TableHead className="w-[15%]">Rate</TableHead>
                        <TableHead className="w-[15%] text-right">Amount</TableHead>
                        <TableHead className="w-[6%]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.service_items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Input
                              value={item.description}
                              onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                              placeholder="Service description"
                              className="uppercase"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.unit}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                              placeholder="NOS"
                              className="uppercase"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.rate}
                              onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.quantity * item.rate)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeServiceItem(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Totals */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>GST Rate (%)</Label>
                  <Input
                    type="number"
                    value={formData.gst_rate}
                    onChange={(e) => handleInputChange('gst_rate', parseFloat(e.target.value) || 0)}
                    min="0"
                    max="100"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Other Charges (Non-Taxable)</Label>
                  <Input
                    type="number"
                    value={formData.other_charges}
                    onChange={(e) => handleInputChange('other_charges', parseFloat(e.target.value) || 0)}
                    min="0"
                  />
                </div>
                <div className="space-y-1 bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Sub Total:</span>
                    <span>{formatCurrency(subTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>GST ({formData.gst_rate}%):</span>
                    <span>{formatCurrency(gstAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Other Charges:</span>
                    <span>{formatCurrency(formData.other_charges || 0)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-1 mt-1">
                    <span>Grand Total:</span>
                    <span>{formatCurrency(grandTotal)}</span>
                  </div>
                </div>
              </div>

              {/* Advance Payment */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="advance_requested"
                    checked={formData.advance_requested}
                    onCheckedChange={(checked) => handleInputChange('advance_requested', checked)}
                  />
                  <Label htmlFor="advance_requested" className="font-medium cursor-pointer">
                    Request Advance Payment
                  </Label>
                </div>
                {formData.advance_requested && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div className="space-y-2">
                      <Label>Advance Amount (₹) *</Label>
                      <Input
                        type="number"
                        value={formData.advance_amount}
                        onChange={(e) => handleInputChange('advance_amount', parseFloat(e.target.value) || 0)}
                        min="0"
                        max={grandTotal}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Payment Due Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.advance_payment_due_date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.advance_payment_due_date ? format(formData.advance_payment_due_date, 'dd/MM/yyyy') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.advance_payment_due_date}
                            onSelect={(date) => handleInputChange('advance_payment_due_date', date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes / Remarks</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Any additional notes..."
                  className="uppercase"
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : (editingSpo ? 'Update SPO' : 'Create & Submit for Approval')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog open={!!viewingSpo} onOpenChange={(open) => { if (!open) setViewingSpo(null); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex justify-between items-center">
                <DialogTitle>Service PO Details - {viewingSpo?.spo_number}</DialogTitle>
                {viewingSpo && ['approved', 'issued', 'complete'].includes(viewingSpo.status) && (
                  <SPOPrint spo={viewingSpo} />
                )}
              </div>
            </DialogHeader>
            {viewingSpo && (
              <div className="space-y-4 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-500">Supplier</Label>
                    <p className="font-medium">{viewingSpo.supplier_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Created Date</Label>
                    <p className="font-medium">{formatDateForDisplay(viewingSpo.created_at?.split('T')[0])}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Status</Label>
                    <Badge className={`${statusColors[viewingSpo.status]} border mt-1`}>
                      {statusLabels[viewingSpo.status]}
                    </Badge>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-500">Service Description</Label>
                  <p className="font-medium">{viewingSpo.service_description}</p>
                </div>

                {/* Service Items */}
                <div>
                  <Label className="text-gray-500 mb-2 block">Service Items</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewingSpo.service_items?.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell>{item.unit}</TableCell>
                            <TableCell className="text-right">{formatRate(item.rate)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span>Sub Total:</span>
                    <span>{formatCurrency(viewingSpo.sub_total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GST ({viewingSpo.gst_rate}%):</span>
                    <span>{formatCurrency(viewingSpo.gst_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Other Charges:</span>
                    <span>{formatCurrency(viewingSpo.other_charges || 0)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Grand Total:</span>
                    <span>{formatCurrency(viewingSpo.grand_total)}</span>
                  </div>
                </div>

                {/* Advance Payment */}
                {viewingSpo.advance_requested && (
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <Label className="text-orange-700 font-medium">Advance Payment Requested</Label>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <span className="text-gray-500 text-sm">Amount:</span>
                        <p className="font-medium">{formatCurrency(viewingSpo.advance_amount)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-sm">Due Date:</span>
                        <p className="font-medium">{formatDateForDisplay(viewingSpo.advance_payment_due_date)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rejection reason if rejected */}
                {viewingSpo.status === 'rejected' && viewingSpo.rejection_reason && (
                  <div className="bg-red-50 p-4 rounded-lg">
                    <Label className="text-red-700 font-medium">Rejection Reason</Label>
                    <p className="mt-1">{viewingSpo.rejection_reason}</p>
                  </div>
                )}

                {/* Notes */}
                {viewingSpo.notes && (
                  <div>
                    <Label className="text-gray-500">Notes</Label>
                    <p>{viewingSpo.notes}</p>
                  </div>
                )}

                {/* Clarification History */}
                {viewingSpo.clarification_history && viewingSpo.clarification_history.length > 0 && (
                  <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageCircle className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-blue-800">Clarification History</span>
                    </div>
                    <div className="space-y-3">
                      {viewingSpo.clarification_history.map((entry, idx) => (
                        <div key={entry.id || idx} className="border-l-2 border-blue-300 pl-3 py-2">
                          <div className="bg-white rounded p-2 mb-2">
                            <p className="text-xs text-blue-600 font-medium">
                              {entry.requested_by_name} requested clarification on {new Date(entry.requested_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                            <p className="text-sm mt-1">{entry.remarks}</p>
                          </div>
                          {entry.response && (
                            <div className="bg-green-50 rounded p-2 ml-4">
                              <p className="text-xs text-green-600 font-medium">
                                Response on {new Date(entry.responded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                              <p className="text-sm mt-1">{entry.response}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Response Input - only show if status is clarification_requested */}
                    {viewingSpo.status === 'clarification_requested' && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <Label className="text-blue-800">Your Response *</Label>
                        <Textarea
                          placeholder="Enter your response to the clarification request..."
                          value={clarificationResponse}
                          onChange={(e) => setClarificationResponse(e.target.value)}
                          className="mt-2 bg-white"
                        />
                        <Button 
                          className="mt-2 bg-blue-600 hover:bg-blue-700"
                          onClick={handleRespondToClarification}
                          disabled={respondingToClarification || !clarificationResponse.trim()}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {respondingToClarification ? 'Submitting...' : 'Respond & Resubmit'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Audit Info */}
                <div className="text-sm text-gray-500 border-t pt-4 space-y-1">
                  <p>Created by: {viewingSpo.created_by_name} on {formatDate(viewingSpo.created_at)}</p>
                  {viewingSpo.approved_at && (
                    <p>{viewingSpo.status === 'rejected' ? 'Rejected' : 'Approved'} by: {viewingSpo.approved_by_name} on {formatDate(viewingSpo.approved_at)}</p>
                  )}
                  {viewingSpo.issued_at && (
                    <p>Issued by: {viewingSpo.issued_by_name} on {formatDate(viewingSpo.issued_at)}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
